import { X } from "lucide-react";
import type { ScenarioKpi, WhatIfPeriod } from "../../../services/api";
import { PERIOD_LABELS, formatAmount } from "../scenarioFormat";

export const MAX_PINNED = 3;

/** A scenario saved for side-by-side comparison. Browser memory only. */
export interface PinnedScenario {
  id: string;
  name: string;
  period: WhatIfPeriod;
  levers: Record<string, number>;
  kpis: ScenarioKpi[];
  headline: string;
}

interface PinnedScenariosProps {
  pins: PinnedScenario[];
  current: { kpis: ScenarioKpi[] } | null;
  onRemove: (id: string) => void;
  onRestore: (pin: PinnedScenario) => void;
}

/** Pinned scenarios next to the live one, measure by measure. */
export function PinnedScenarios({
  pins,
  current,
  onRemove,
  onRestore,
}: PinnedScenariosProps) {
  if (pins.length === 0) return null;
  const reference = current?.kpis ?? pins[0]?.kpis ?? [];
  return (
    <div className="panel-table-wrap">
      <table className="panel-table pinned-scenarios">
        <thead>
          <tr>
            <th scope="col">Measure</th>
            <th scope="col" className="numeric">Today</th>
            {pins.map((pin) => (
              <th scope="col" className="numeric" key={pin.id}>
                <span className="pin-heading">
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => onRestore(pin)}
                    title="Load these inputs"
                  >
                    {pin.name}
                  </button>
                  <button
                    type="button"
                    className="icon-button"
                    aria-label={`Remove ${pin.name}`}
                    onClick={() => onRemove(pin.id)}
                  >
                    <X size={12} />
                  </button>
                </span>
                <small>{PERIOD_LABELS[pin.period]}</small>
              </th>
            ))}
            {current && <th scope="col" className="numeric">Live scenario</th>}
          </tr>
        </thead>
        <tbody>
          {reference.map((measure) => (
            <tr key={measure.id}>
              <th scope="row">{measure.label}</th>
              <td className="numeric">
                {formatAmount(measure.baseline, measure.unit)}
              </td>
              {pins.map((pin) => {
                const match = pin.kpis.find((item) => item.id === measure.id);
                return (
                  <td className="numeric" key={pin.id}>
                    {formatAmount(match?.scenario, measure.unit)}
                  </td>
                );
              })}
              {current && (
                <td className="numeric live">
                  {formatAmount(
                    current.kpis.find((item) => item.id === measure.id)?.scenario,
                    measure.unit,
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
